/* eslint-disable */

import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import document from 'global/document';
import Spinner from './Spinner';
import * as messageBus from '@wh/message-bus';
import feedbackMessageBus from '../utils/feedbackMessageBus';

import './App.scss';
import {
  overrideRedirect,
  analytics,
  handleRegistrationButtonClick,
  usePoolingUserLogin,
  useDepositRedirect,
  useOverwriteFooterValues,
  useLoginTracking,
  useLoginTrackingUK,
  useQueryParams,
} from '../utils';
import { useServerData } from '../state/serverDataContext';
import { useLocale } from '../utils/locales';

const Banner = loadable(() => import(/* webpackChunkName: 'Banner' */ './Banner'), { fallback: <div>Loading...</div> });

const LoadTermsConditions = loadable(() => import(/* webpackChunkName: 'TermsConditions' */ './TermsConditions'), {
  fallback: <div>Loading...</div>,
});

const LoadCookieBar = loadable(() => import(/* webpackChunkName: 'CookieBar' */ './CookieBar'), {
  fallback: <div>Loading...</div>,
});

const LoadTopBets = loadable(() => import(/* webpackChunkName: 'TopBets' */ './TopBets'), {
  fallback: <div>Loading...</div>,
});

const TeaserContainer = loadable(() => import(/* webpackChunkName: 'TeaserContainer' */ './TeaserContainer'), {
  fallback: <div>Loading...</div>,
});

const LoadFooter = loadable(() => import(/* webpackChunkName: 'Footer' */ './Footer'), {
  fallback: <div>Loading...</div>,
});

const MoreVerticals = loadable(() => import(/* webpackChunkName: 'MoreVerticals' */ './MoreVerticals'), {
  fallback: <div>Loading...</div>,
});

const App = () => {
  const { redirectUrl, environment, noLogin, domain, locale, shouldRedirect, serverError, isMobile } = useServerData(
    data => ({
      redirectUrl: data.pageData?.pageSettings?.redirect.replace(
        '{env}',
        data.config.environment === 'prod' ? '' : '-pp1'
      ),
      locale: data.locale,
      domain: data.domain,
      noLogin: data.nologin || false,
      serverError: data.serverError || false,
      shouldRedirect: data.shouldRedirect || false,
      environment: data.config?.environment || 'prod',
      isMobile: data.isMobile,
    })
  );

  const [loader, showLoader] = useState(false);
  const onLoadingChange = status => showLoader(status);

  const { isSpain, isItaly, isFinland, isIreland, isEngland } = useLocale(locale);

  usePoolingUserLogin(noLogin, onLoadingChange, redirectUrl);
  useDepositRedirect(locale, redirectUrl);
  useOverwriteFooterValues();
  useLoginTracking(locale, environment);
  useQueryParams('action');

  useEffect(() => {
    // Ensure the WH object and messageBus are available on the global window object
    window.WH = window.WH || {};
    window.WH.messageBus = messageBus;

    // allow open feedback form from the link in the footer
    return feedbackMessageBus();
  }, []);

  useEffect(() => {
    overrideRedirect(locale);
    analytics(environment, shouldRedirect, locale);
    useLoginTrackingUK(locale, environment, isMobile);

    const headerActionArea = document.querySelector('.sb-action-area');
    if (headerActionArea && (isItaly || isFinland)) {
      const handleClick = handleRegistrationButtonClick(locale);
      headerActionArea.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [environment]);

  useEffect(() => {
    if (serverError) {
      console.warn('An error occurred when fetching ContentStack data.');
    }
  }, [serverError]);

  const termsAndConditionsHidden = isEngland || isIreland || isSpain;

  const cookieBannerIsNotVisible = isItaly || isSpain; // for Italy and Spain there is cookie banner loaded by utag.js

  const teaserContainerVisible = isSpain || isEngland || isIreland;

  return (
    <div className="app">
      {loader && <Spinner />}
      <LoadCookieBar environment={environment} cookieBannerIsNotVisible={cookieBannerIsNotVisible} />
      <div className="main" data-testid="inter-app">
        <Banner />
        {!termsAndConditionsHidden ? <LoadTermsConditions /> : null}
        <MoreVerticals />
        {teaserContainerVisible && <TeaserContainer />}
      </div>
    </div>
  );
};

export default App;
